import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Anchor,
  ArrowRight,
  BadgePercent,
  BedDouble,
  Car,
  Fuel,
  Headphones,
  Mail,
  MapPin,
  Menu,
  Package,
  Phone,
  Plane,
  ShieldCheck,
  Ship,
  Star,
  Users,
  X,
} from 'lucide-react';
import TravelSearch, { buildParams, daysFromNow } from '../components/home/TravelSearch';
import { seededRandomPrice } from '../flights/utils/pricing';
import { MAIN_PHONE_DISPLAY, MAIN_PHONE_TEL } from '../constants/contact';
import {
  ADDRESS_LINE1,
  ADDRESS_LINE2,
  AFFILIATE_DISCLOSURE,
  COMPANY_EMAIL,
  COMPANY_NAME,
  FOOTER_DISCLAIMER,
  SITE_NAME,
} from '../constants/site';
import '../styles/travel-home.css';

const img = (file) => `${process.env.PUBLIC_URL}/images/${file}`;

const NAV = [
  { href: '#services', label: 'Services' },
  { href: '#destinations', label: 'Destinations' },
  { href: '#cars', label: 'Cars' },
  { href: '#cruises', label: 'Cruises' },
  { href: '#packages', label: 'Packages' },
  { href: '#about', label: 'About Us' },
  { href: '#contact', label: 'Contact' },
];

const SERVICES = [
  {
    id: 'flights',
    title: 'Flights',
    text: 'Domestic and international fares on major airlines, including round trip, one way and multi-city itineraries.',
    image: img('airplane.jpg'),
    Icon: Plane,
  },
  {
    id: 'hotels',
    title: 'Hotels',
    text: 'Hotels, resorts and boutique stays to suit any budget, with help choosing the right room and location.',
    image: img('hotel-room.jpg'),
    Icon: BedDouble,
  },
  {
    id: 'cars',
    title: 'Car Rentals',
    text: 'Economy cars to SUVs and vans from trusted rental brands, at airports and city locations.',
    image: img('rental-car.jpg'),
    Icon: Car,
  },
  {
    id: 'cruises',
    title: 'Cruises',
    text: 'Caribbean, Mediterranean, Alaska and more, with help picking the right ship and cabin.',
    image: img('mumbai.jpg'),
    Icon: Ship,
  },
  {
    id: 'packages',
    title: 'Travel Packages',
    text: 'Flight and hotel bundles, honeymoons, family holidays and group trips planned around you.',
    image: img('vacation-package.jpg'),
    Icon: Package,
  },
];

const price = (seed) => seededRandomPrice(`home-${seed}`);

const DESTINATIONS = [
  { city: 'Paris', country: 'France', image: img('paris.jpg') },
  { city: 'London', country: 'United Kingdom', image: img('london.jpg') },
  { city: 'Dubai', country: 'United Arab Emirates', image: img('dubai.jpg') },
  { city: 'Tokyo', country: 'Japan', image: img('tokyo.jpg') },
  { city: 'Cancun', country: 'Mexico', image: img('tropical-beach.jpg') },
  { city: 'Calgary', country: 'Canada', image: img('miami-beach.jpg') },
].map((d) => ({ ...d, from: price(`dest-${d.city}`) }));

const CAR_DEALS = [
  { type: 'Economy', example: 'Nissan Versa or similar', seats: 5, bags: 1, location: 'Los Angeles' },
  { type: 'Compact', example: 'Toyota Corolla or similar', seats: 5, bags: 2, location: 'Miami' },
  { type: 'SUV', example: 'Ford Explorer or similar', seats: 5, bags: 3, location: 'Orlando' },
  { type: 'Luxury', example: 'BMW 5 Series or similar', seats: 5, bags: 2, location: 'Las Vegas' },
  { type: 'Van', example: 'Chrysler Pacifica or similar', seats: 7, bags: 4, location: 'Chicago' },
  { type: 'Convertible', example: 'Ford Mustang or similar', seats: 4, bags: 1, location: 'San Francisco' },
].map((c) => ({ ...c, from: price(`car-${c.type}`) }));

const CRUISE_DEALS = [
  { title: 'Western Caribbean', port: 'Miami', region: 'Caribbean', nights: 7, stops: 'Cozumel · Roatan · Costa Maya' },
  { title: 'Greek Isles & Italy', port: 'Rome', region: 'Mediterranean', nights: 10, stops: 'Naples · Santorini · Mykonos' },
  { title: 'Alaska Inside Passage', port: 'Seattle', region: 'Alaska', nights: 7, stops: 'Juneau · Skagway · Ketchikan' },
  { title: 'Norwegian Fjords', port: 'Southampton', region: 'Northern Europe', nights: 12, stops: 'Bergen · Geiranger · Flam' },
].map((c) => ({ ...c, from: price(`cruise-${c.title}`) }));

const PACKAGES = [
  {
    title: 'Maldives Overwater Escape',
    destination: 'Male',
    nights: '6 nights',
    includes: 'Flights + overwater villa',
    image: img('mumbai.jpg'),
  },
  {
    title: 'Caribbean Beach Holiday',
    destination: 'Cancun',
    nights: '5 nights',
    includes: 'Flights + beachfront resort',
    image: img('vacation-package.jpg'),
  },
  {
    title: 'Alpine Mountain Retreat',
    destination: 'Zurich',
    nights: '4 nights',
    includes: 'Flights + mountain lodge',
    image: img('mountain-lake.jpg'),
  },
].map((p) => ({ ...p, from: price(`pkg-${p.title}`) }));

const WHY_US = [
  { Icon: Headphones, title: '24/7 support', text: 'Real travel advisors before, during and after your trip.' },
  { Icon: BadgePercent, title: 'Better fares by phone', text: 'Ask about fares and bundles that are not shown online.' },
  { Icon: ShieldCheck, title: 'Clear terms', text: 'Supplier rules, fees and cancellation policies explained up front.' },
  { Icon: Star, title: 'One place for everything', text: 'Flights, hotels, cars, cruises and packages handled by one team.' },
];

const POLICY_LINKS = [
  { to: '/flight/about', label: 'About Us' },
  { to: '/flight/support', label: 'Contact Us' },
  { to: '/flight/privacy', label: 'Privacy Policy' },
  { to: '/flight/terms', label: 'Terms & Conditions' },
  { to: '/flight/cookies', label: 'Cookie Policy' },
  { to: '/flight/cancellation-refund', label: 'Cancellation & Refund' },
  { to: '/flight/service-fees', label: 'Service Fees' },
  { to: '/flight/advertiser-disclosure', label: 'Advertiser Disclosure' },
];

function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return undefined;
    const sync = () =>
      document.documentElement.style.setProperty('--th-header-h', `${header.offsetHeight}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(header);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const close = () => setMenuOpen(false);

  return (
    <header className="th-header" ref={headerRef}>
      <div className="th-disclosure" role="note">
        <div className="th-container th-disclosure__inner">
          <p>{AFFILIATE_DISCLOSURE}</p>
          <a href={`tel:${MAIN_PHONE_TEL}`} className="th-disclosure__phone">
            <Phone size={14} aria-hidden />
            24/7: {MAIN_PHONE_DISPLAY}
          </a>
        </div>
      </div>
      <div className="th-nav">
        <div className="th-container th-nav__inner">
          <Link to="/" className="th-logo" onClick={close}>
            <span className="th-logo__mark">
              <Plane size={20} aria-hidden />
            </span>
            <span className="th-logo__text">
              <strong>{SITE_NAME}</strong>
              <small>Flights · Hotels · Cars · Cruises</small>
            </span>
          </Link>

          <nav id="th-menu" className={`th-nav__links${menuOpen ? ' is-open' : ''}`} aria-label="Main">
            {NAV.map((item) => (
              <a key={item.href} href={item.href} onClick={close}>
                {item.label}
              </a>
            ))}
            <a href={`tel:${MAIN_PHONE_TEL}`} className="th-btn th-btn--primary th-nav__call" onClick={close}>
              <Phone size={16} aria-hidden />
              {MAIN_PHONE_DISPLAY}
            </a>
          </nav>

          <button
            type="button"
            className="th-nav__toggle"
            onClick={() => setMenuOpen((o) => !o)}
            aria-expanded={menuOpen}
            aria-controls="th-menu"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
    </header>
  );
}

export default function TravelHomePage() {
  return (
    <div className="th-root">
      <SiteHeader />

      <main>
        <section className="th-hero" style={{ backgroundImage: `url(${img('hero.jpg')})` }}>
          <div className="th-hero__overlay" aria-hidden="true" />
          <div className="th-container th-hero__inner">
            <p className="th-eyebrow th-eyebrow--light">Your all-in-one travel agency</p>
            <h1>Flights, hotels, cars, cruises and packages, booked by people who know travel</h1>
            <p className="th-hero__lead">
              Search fares and stays below, or call our advisors for help with anything from a quick weekend away to a
              multi-city trip.
            </p>
            <TravelSearch />
          </div>
        </section>

        <section id="services" className="th-section">
          <div className="th-container">
            <div className="th-section__head">
              <p className="th-eyebrow">What we book</p>
              <h2>Everything for your trip, in one place</h2>
            </div>
            <div className="th-services">
              {SERVICES.map(({ id, title, text, image, Icon }) => (
                <article key={id} className="th-service">
                  <div className="th-service__media">
                    <img src={image} alt="" loading="lazy" />
                    <span className="th-service__icon">
                      <Icon size={22} aria-hidden />
                    </span>
                  </div>
                  <div className="th-service__body">
                    <h3>{title}</h3>
                    <p>{text}</p>
                    <a href={`tel:${MAIN_PHONE_TEL}`} className="th-link">
                      Call to book <ArrowRight size={16} aria-hidden />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="destinations" className="th-section th-section--tint">
          <div className="th-container">
            <div className="th-section__head">
              <p className="th-eyebrow">Popular right now</p>
              <h2>Top destinations</h2>
            </div>
            <div className="th-dest">
              {DESTINATIONS.map((d) => (
                <Link
                  key={d.city}
                  to={`/flight/search/flights?${buildParams({
                    to: d.city,
                    depart: daysFromNow(14),
                    return: daysFromNow(21),
                    adults: 1,
                    children: 0,
                    cabin: 'economy',
                    trip: 'roundtrip',
                    passengers: '1',
                  })}`}
                  className="th-dest__card"
                >
                  <img src={d.image} alt="" loading="lazy" />
                  <span className="th-dest__shade" aria-hidden="true" />
                  <span className="th-dest__text">
                    <strong>{d.city}</strong>
                    <small>{d.country}</small>
                    <em>Flights from ${d.from}*</em>
                  </span>
                </Link>
              ))}
            </div>
            <p className="th-note">*Sample round-trip economy fares. Prices change with dates and availability.</p>
          </div>
        </section>

        <section id="cars" className="th-section">
          <div className="th-container">
            <div className="th-section__head th-section__head--row">
              <div>
                <p className="th-eyebrow">Car rentals</p>
                <h2>Pick up a car where you land</h2>
              </div>
              <img src={img('rental-car.jpg')} alt="" className="th-section__thumb" loading="lazy" />
            </div>
            <div className="th-deals">
              {CAR_DEALS.map((c) => (
                <Link
                  key={c.type}
                  className="th-deal"
                  to={`/flight/search/cars?${buildParams({
                    pickup: c.location,
                    dropoff: c.location,
                    pickupDate: daysFromNow(7),
                    dropoffDate: daysFromNow(10),
                    carType: c.type,
                  })}`}
                >
                  <span className="th-deal__icon">
                    <Car size={22} aria-hidden />
                  </span>
                  <span className="th-deal__body">
                    <strong>{c.type}</strong>
                    <small>{c.example}</small>
                    <span className="th-deal__meta">
                      <span>
                        <Users size={14} aria-hidden /> {c.seats} seats
                      </span>
                      <span>
                        <Fuel size={14} aria-hidden /> Automatic
                      </span>
                      <span>
                        <MapPin size={14} aria-hidden /> {c.location}
                      </span>
                    </span>
                  </span>
                  <span className="th-deal__price">
                    <strong>${c.from}</strong>
                    <small>per day*</small>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section id="cruises" className="th-section th-section--tint">
          <div className="th-container">
            <div className="th-section__head">
              <p className="th-eyebrow">Cruises</p>
              <h2>Popular sailings</h2>
            </div>
            <div className="th-cruises">
              {CRUISE_DEALS.map((c) => (
                <article key={c.title} className="th-cruise">
                  <span className="th-chip">{c.nights} nights</span>
                  <h3>{c.title}</h3>
                  <p className="th-cruise__route">
                    <Anchor size={15} aria-hidden /> Round trip from {c.port}
                  </p>
                  <p className="th-cruise__stops">{c.stops}</p>
                  <div className="th-package__foot">
                    <span>
                      from <strong>${c.from}</strong> pp*
                    </span>
                    <Link
                      className="th-btn th-btn--outline"
                      to={`/flight/search/cruises?${buildParams({
                        port: c.port,
                        destination: c.region,
                        depart: daysFromNow(30),
                        adults: 2,
                        children: 0,
                        guests: '2',
                      })}`}
                    >
                      View sailings
                    </Link>
                  </div>
                </article>
              ))}
            </div>
            <p className="th-note">*Sample prices. Final fares depend on dates, cabin and availability.</p>
          </div>
        </section>

        <section id="packages" className="th-section">
          <div className="th-container">
            <div className="th-section__head">
              <p className="th-eyebrow">Holiday packages</p>
              <h2>Bundle your flight and hotel</h2>
            </div>
            <div className="th-packages">
              {PACKAGES.map((p) => (
                <article key={p.title} className="th-package">
                  <img src={p.image} alt="" loading="lazy" />
                  <div className="th-package__body">
                    <span className="th-chip">{p.nights}</span>
                    <h3>{p.title}</h3>
                    <p>{p.includes}</p>
                    <div className="th-package__foot">
                      <span>
                        from <strong>${p.from}</strong> pp*
                      </span>
                      <Link
                        className="th-btn th-btn--outline"
                        to={`/flight/search/packages?${buildParams({
                          destination: p.destination,
                          depart: daysFromNow(14),
                          return: daysFromNow(21),
                          adults: 2,
                          children: 0,
                          travelers: '2',
                        })}`}
                      >
                        View deals
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <p className="th-note">*Per person, based on two adults sharing. Subject to availability.</p>
          </div>
        </section>

        <section className="th-section th-section--dark">
          <div className="th-container">
            <div className="th-section__head">
              <p className="th-eyebrow th-eyebrow--light">Why travel with us</p>
              <h2>Real advisors, not just a search box</h2>
            </div>
            <div className="th-why">
              {WHY_US.map(({ Icon, title, text }) => (
                <div key={title} className="th-why__item">
                  <span className="th-why__icon">
                    <Icon size={24} aria-hidden />
                  </span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="about" className="th-section">
          <div className="th-container th-about">
            <img src={img('travel-planning.jpg')} alt="Hand holding a small globe" loading="lazy" />
            <div>
              <p className="th-eyebrow">About us</p>
              <h2>Operated by {COMPANY_NAME}</h2>
              <p>
                {SITE_NAME} is operated by {COMPANY_NAME}, an independent travel advisory company affiliated with Fora
                Travel. We help travelers plan and book flights, hotels, car rentals, cruises, vacation packages and other travel
                experiences, and we stay on hand after booking for changes and questions.
              </p>
              <p>
                We act as an intermediary between you and travel suppliers such as airlines and hotels. Every
                reservation follows that supplier's availability, fare rules and cancellation policy, and we explain
                those terms before you pay.
              </p>
            </div>
          </div>
        </section>

        <section id="contact" className="th-section th-section--tint">
          <div className="th-container">
            <div className="th-section__head">
              <p className="th-eyebrow">Contact</p>
              <h2>Talk to a travel advisor</h2>
            </div>
            <div className="th-contact">
              <a href={`tel:${MAIN_PHONE_TEL}`} className="th-contact__card">
                <Phone size={24} aria-hidden />
                <span>
                  <small>Call 24/7</small>
                  <strong>{MAIN_PHONE_DISPLAY}</strong>
                </span>
              </a>
              <a href={`mailto:${COMPANY_EMAIL}`} className="th-contact__card">
                <Mail size={24} aria-hidden />
                <span>
                  <small>Email</small>
                  <strong>{COMPANY_EMAIL}</strong>
                </span>
              </a>
              <div className="th-contact__card">
                <MapPin size={24} aria-hidden />
                <span>
                  <small>{COMPANY_NAME}</small>
                  <strong>
                    {ADDRESS_LINE1}
                    <br />
                    {ADDRESS_LINE2}
                  </strong>
                </span>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="th-footer">
        <div className="th-container">
          <div className="th-footer__top">
            <div>
              <Link to="/" className="th-logo th-logo--light">
                <span className="th-logo__mark">
                  <Plane size={20} aria-hidden />
                </span>
                <span className="th-logo__text">
                  <strong>{SITE_NAME}</strong>
                  <small>Flights · Hotels · Cars · Cruises</small>
                </span>
              </Link>
              <address className="th-footer__address">
                {COMPANY_NAME}
                <br />
                {ADDRESS_LINE1}, {ADDRESS_LINE2}
                <br />
                <a href={`tel:${MAIN_PHONE_TEL}`}>{MAIN_PHONE_DISPLAY}</a> ·{' '}
                <a href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a>
              </address>
            </div>
            <nav className="th-footer__links" aria-label="Policies">
              {POLICY_LINKS.map((l) => (
                <Link key={l.to} to={l.to}>
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="th-footer__disclaimer">
            <h2>Disclaimer</h2>
            {FOOTER_DISCLAIMER.map((para) => (
              <p key={para}>{para}</p>
            ))}
            <p>
              Prices shown on this site are examples and may not be available at the time of booking. Final prices,
              including taxes and fees, are confirmed before payment. Airline and hotel names and logos belong to their
              owners; their use does not imply endorsement.
            </p>
          </div>

          <p className="th-footer__copy">
            &copy; {new Date().getFullYear()} {SITE_NAME}. Operated by {COMPANY_NAME}. All rights reserved.
          </p>
          <p className="th-footer__credit">
            City and country data from{' '}
            <a href="https://www.geonames.org/" target="_blank" rel="noopener noreferrer">
              GeoNames
            </a>{' '}
            (CC BY 4.0).
          </p>
        </div>
      </footer>

      <a href={`tel:${MAIN_PHONE_TEL}`} className="th-callbar">
        <Phone size={18} aria-hidden />
        Call 24/7 for the best fares: <strong>{MAIN_PHONE_DISPLAY}</strong>
      </a>
    </div>
  );
}
